import moment from 'moment';
import {storage, STORAGE_TTL} from './storage';
import {ConversationStatus} from './types';
import {
  CONVERSATION_STORAGE_PREFIX,
  StoredConversationState,
} from './conversation';

export interface StoredConversation {
  id: string;
  timestamp: number;
  data: Partial<StoredConversationState>;
}

export const DEFAULT_STORED_CONVERSATION_TTL: STORAGE_TTL = {
  value: 6,
  unit: 'hours',
};

// returns stored collections, clearing those older than the ttl
export const storedConversations = ({
  limit = 0,
  ttl = DEFAULT_STORED_CONVERSATION_TTL,
  includeInactive = false,
}: {
  limit?: number;
  ttl?: STORAGE_TTL;
  includeInactive?: boolean;
} = {}): StoredConversation[] => {
  const conversations: StoredConversation[] = [];

  if (!storage.isStorageAvailable()) {
    return conversations;
  }

  try {
    let offset: number | undefined;
    if (ttl) {
      offset = moment(Date.now()).subtract(ttl.value, ttl.unit).valueOf();
    }

    const keys = storage.getKeyMatches(`${CONVERSATION_STORAGE_PREFIX}_*`);
    keys.forEach((key) => {
      if (!key.startsWith(CONVERSATION_STORAGE_PREFIX)) {
        return;
      }

      const item = storage.get(key);
      if (!item) {
        return;
      }

      const stored = JSON.parse(item);

      if (!('timestamp' in stored)) {
        // if no timestamp in data specified, remove it
        storage.remove(key);
        return;
      }

      if (offset && stored.timestamp < offset) {
        // if stored data older than TTL offset, remove it
        storage.remove(key);
        return;
      }

      if (
        stored.data?.status !== ConversationStatus.Active &&
        !includeInactive
      ) {
        return;
      }

      conversations.push({
        id: key.replace(`${CONVERSATION_STORAGE_PREFIX}_`, ''),
        timestamp: stored.timestamp,
        data: stored.data,
      });
    });

    conversations.sort((a, b) => a.timestamp - b.timestamp);
    if (limit > 0) {
      return conversations.slice(0, limit);
    }

    return conversations;
  } catch (e) {
    return conversations;
  }
};
